import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  ShipmentLocationViewModel,
  ShipmentRouteViewModel,
} from '@components/shipment-route-list/shipment-route-map/shipment-route-map.component';
import {
  ShipmentOptionEditComponent,
  ShipmentOptionFormGroup,
} from './shipment-option-edit/shipment-option-edit.component';
import { ShipmentDeviceEditComponent } from './shipment-device-edit/shipment-device-edit.component';
import { MatDividerModule } from '@angular/material/divider';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { firstValueFrom } from 'rxjs';
import { DeviceSettingListViewModel } from '../shipment-list.component';
import { ShipmentRouteEditComponent } from './shipment-route-edit/shipment-route-edit.component';
import { ShipmentDeviceSettingService } from '@services/api/shipment-device-setting.service';
import { DeviceSettingResponse } from '@entities/deviceSetting';
import { DeviceSettingViewModel } from '@components/device-setting-list/device-setting-edit-dialog/device-setting-edit-dialog.component';
import { ShipmentService } from '@services/api/shipment.service';
import { AvailableDeviceResponse } from '@entities/shipment';

/** 繰り返しタイプ */
export type RepeatType = '' | 'once' | 'repetition';

/** ダイアログのViewModel */
export type ShipmentEditorDialogViewModel = {
  /** 更新モードか */
  updating: boolean;
  /** 読み取り専用モードか */
  isReadOnly: boolean;
  /** 配送設定ID */
  shipmentId?: string;
  /** 編集対象 */
  target?: EditedShipmentViewModel;
  /** ルート一覧 */
  routeList: ShipmentRouteViewModel[];
  /** 位置情報一覧 */
  locationList: ShipmentLocationViewModel[];
  /** デバイス設定一覧 */
  deviceSettingList: DeviceSettingListViewModel[];
  /** 配送設定一覧 */
  shipmentList: ShipmentNameViewModel[];
  /** 元のデバイス設定 */
  originalDeviceSetting?: DeviceSettingListViewModel;
};

/** 配送名重複チェック用ViewModel */
export type ShipmentNameViewModel = {
  /** 配送設定ID */
  shipmentId: string;
  /** 配送設定名 */
  shipmentName: string;
};

/** 配送設定フォーム */
export type ShipmentFormGroup = FormGroup<{
  /** オプション */
  shipmentOption: ShipmentOptionFormGroup;
  /** ルート */
  shipmentRoute: ShipmentRouteFormGroup;
  /** デバイス */
  shipmentDevice: ShipmentDeviceFormGroup;
}>;

/** ルートフォーム */
export type ShipmentRouteFormGroup = FormGroup<{
  /** 出発地 */
  startLocationId: FormControl<string | null>;
  /** 到着地 */
  arrivalLocationId: FormControl<string | null>;
}>;

/** デバイスフォーム */
export type ShipmentDeviceFormGroup = FormGroup<{
  /** デバイス種類 */
  deviceType: FormControl<string | null>;
  /** デバイスID */
  deviceId: FormControl<string | null>;
}>;

/** 編集結果ViewModel */
export type EditedShipmentViewModel = {
  /** オプション */
  shipmentOption: OptionFormViewModel;
  /** ルート */
  shipmentRoute: RouteFormViewModel;
  /** デバイス */
  shipmentDevice: DeviceFormViewModel;
  /** デバイス詳細設定 */
  shipmentDeviceSettingList?: DeviceSettingViewModel[];
};

/** オプションViewModel */
export type OptionFormViewModel = {
  /** 配送設定名 */
  shipmentName: string;
  /** 繰り返し作成 */
  repeatType: RepeatType;
};

/** ルートViewModel */
export type RouteFormViewModel = {
  /** 出発地 */
  startLocationId: string | null;
  /** 到着地 */
  arrivalLocationId: string | null;
};

/** デバイスViewModel */
export type DeviceFormViewModel = {
  /** デバイス種類 */
  deviceType: string;
  /** デバイスID */
  deviceId: string;
  /** デバイス名 */
  deviceName: string;
  /** デバイス設定グループ名 */
  deviceSettingGroupName?: string;
  /** 削除済みデバイスか */
  deleted?: boolean;
};

@Component({
  selector: 'app-shipment-edit-dialog',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIconModule,
    CommonModule,
    ShipmentOptionEditComponent,
    ShipmentRouteEditComponent,
    ShipmentDeviceEditComponent,
    MatDividerModule,
  ],
  templateUrl: './shipment-edit-dialog.component.html',
  styleUrl: './shipment-edit-dialog.component.scss',
})
export class ShipmentEditDialogComponent {
  /** 更新モードか */
  public updating: boolean;
  /** 読み取り専用モードか */
  public isReadOnly: boolean;
  /** デバイス一覧 */
  public deviceSettingList: DeviceSettingListViewModel[] = [];
  /** 配送別デバイス詳細設定 */
  public shipmentDeviceSettings: Record<string, DeviceSettingResponse> = {};
  /** ルート一覧 */
  public routeList: ShipmentRouteViewModel[] = [];
  /** 位置情報一覧 */
  public locationList: ShipmentLocationViewModel[] = [];
  /** 配送設定フォーム */
  public formGroup: ShipmentFormGroup;
  /** デバイス種類の変更検知用 */
  public preDeviceType = '';
  /** デバイス設定読込中 */
  public loadingDeviceSetting = false;

  /** 送信不可か */
  public get submitDisabled(): boolean {
    const selectedDeviceId =
      this.formGroup?.controls.shipmentDevice.controls.deviceId.value;
    return (
      this.formGroup.invalid ||
      this.isReadOnly ||
      this.loadingDeviceSetting ||
      !selectedDeviceId
    );
  }

  /** 配送設定ID */
  private shipmentId: string | undefined;
  /** 元のデバイス設定 */
  private readonly originalDevice?: DeviceSettingListViewModel;
  /** 変更後のデバイス設定 */
  private updatedDeviceSettings: Record<string, DeviceSettingViewModel> = {};

  constructor(
    @Inject(MAT_DIALOG_DATA) options: ShipmentEditorDialogViewModel,
    private dialogRef: MatDialogRef<ShipmentEditDialogComponent>,
    private dialog: MatDialog,
    private shipmentDeviceSettingService: ShipmentDeviceSettingService,
    private shipmentService: ShipmentService,
  ) {
    this.updating = options.updating;
    this.isReadOnly = options.isReadOnly;
    this.routeList = options.routeList;
    this.locationList = options.locationList;
    this.deviceSettingList = options.deviceSettingList;
    this.originalDevice = options.originalDeviceSetting;
    this.shipmentId = options?.shipmentId;

    const target = options.target;

    if (options?.shipmentId) {
      this.shipmentDeviceSettingService
        .findList(options.shipmentId)
        .subscribe((deviceSettingList) => {
          this.shipmentDeviceSettings = {};
          deviceSettingList.forEach(
            (deviceSetting) =>
              (this.shipmentDeviceSettings[deviceSetting.serialNo] =
                deviceSetting),
          );
        });
    }

    const shipmentOptionFormGroup: ShipmentOptionFormGroup = new FormGroup({
      shipmentName: new FormControl<string>(
        target?.shipmentOption.shipmentName || '',
        {
          nonNullable: true,
          validators: [
            Validators.required,
            this.shipmentDuplicationValidator(
              options.shipmentList,
              options.shipmentId,
            ),
          ],
        },
      ),
      repeatType: new FormControl<RepeatType>(
        target?.shipmentOption.repeatType ?? '',
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
    });

    const shipmentRouteFormGroup: ShipmentRouteFormGroup = new FormGroup({
      startLocationId: new FormControl<string | null>(
        target?.shipmentRoute.startLocationId ?? null,
        { validators: [Validators.required] },
      ),
      arrivalLocationId: new FormControl<string | null>(
        target?.shipmentRoute.arrivalLocationId ?? null,
        { validators: [Validators.required] },
      ),
    });

    const shipmentDeviceFormGroup: ShipmentDeviceFormGroup = new FormGroup({
      deviceType: new FormControl<string | null>(
        target?.shipmentDevice.deviceType || null,
        { validators: [Validators.required] },
      ),
      deviceId: new FormControl<string | null>(
        target?.shipmentDevice.deviceId || null,
        { validators: [Validators.required] },
      ),
    });
    this.preDeviceType = target?.shipmentDevice.deviceType ?? '';

    this.formGroup = new FormGroup({
      shipmentOption: shipmentOptionFormGroup,
      shipmentRoute: shipmentRouteFormGroup,
      shipmentDevice: shipmentDeviceFormGroup,
    });

    const isDeletedDevice = !!target?.shipmentDevice.deleted;
    if (isDeletedDevice) {
      this.updateDeviceListForDeleted();
    } else if (this.updating && this.originalDevice) {
      if (this.isReadOnly) {
        this.updateDeviceListByOwn(this.originalDevice);
      }
    }

    if (this.isReadOnly) {
      this.formGroup.disable({ emitEvent: false });
      return;
    }

    if (!isDeletedDevice) {
      // 編集可能な配送では、契約状態を含めて利用可能なデバイスのみを候補表示する。
      this.updateAvailableDeviceList();
    }

    this.formGroup.valueChanges.subscribe((v) => {
      if (
        v.shipmentDevice?.deviceType &&
        this.preDeviceType !== v.shipmentDevice?.deviceType
      ) {
        this.formGroup.controls.shipmentDevice.controls.deviceId.setValue(
          null,
          {
            emitEvent: false,
          },
        );
        this.preDeviceType = v.shipmentDevice?.deviceType;
      }
    });
  }

  /** 追加・更新を確定する */
  public async submit(): Promise<void> {
    if (this.submitDisabled) {
      return;
    }

    const shipmentName =
      this.formGroup.controls.shipmentOption.controls.shipmentName.value || '';
    const operation = this.updating ? '更新' : '新規追加';
    const options: AlertDialogOption = {
      type: 'warn',
      title: 'Confirm',
      message: `配送設定「${shipmentName}」を${operation}してよろしいですか？`,
      operation: operation,
    };

    const result: boolean = await firstValueFrom(
      this.dialog
        .open(AlertDialogComponent, {
          width: '600px',
          data: options,
        })
        .afterClosed(),
    );

    if (!result) {
      return;
    }

    const shipment: EditedShipmentViewModel =
      this.convertShipmentFormGroupToViewModel(
        this.formGroup,
        this.updatedDeviceSettings,
      );
    this.dialogRef.close(shipment);
  }

  /** デバイス候補を更新する */
  public updateAvailableDeviceList(): void {
    // 取得完了前に旧候補で操作されないよう、読み込み中を明示する。
    this.loadingDeviceSetting = true;
    const currentDeviceId =
      this.formGroup.controls.shipmentDevice.controls.deviceId.value ||
      undefined;
    this.shipmentService
      .findAvailableDeviceList({
        deviceId: currentDeviceId,
      })
      .subscribe({
        next: (availableDeviceList) => {
          this.deviceSettingList =
            this.convertAvailableDeviceList(availableDeviceList);
          this.loadingDeviceSetting = false;
        },
        error: () => {
          // 取得失敗時は候補を空にして不正なデバイス選択を防止する。
          this.deviceSettingList = [];
          this.loadingDeviceSetting = false;
          const options: AlertDialogOption = {
            type: 'error',
            message:
              '利用可能なデバイス一覧の取得に失敗しました。\nしばらくしてからもう一度お試しください。',
          };
          this.dialog.open(AlertDialogComponent, {
            width: '600px',
            data: options,
          });
        },
      });
  }

  /** 自分自身のデバイス一覧を設定する */
  public updateDeviceListByOwn(
    originalDevice: DeviceSettingListViewModel,
  ): void {
    this.deviceSettingList = [
      {
        deviceName: originalDevice.deviceName,
        deviceId: originalDevice.deviceId,
        deviceTypeId: originalDevice.deviceTypeId,
        deviceTypeDisplayName: originalDevice.deviceTypeDisplayName,
        deviceSettingGroupName: originalDevice.deviceSettingGroupName ?? '',
        lastCommunicationAt: '',
        status: '',
        battery: 0,
        tagNameList: [],
      },
    ];
  }

  /** 削除済みデバイス表示に切り替える */
  public updateDeviceListForDeleted(): void {
    this.deviceSettingList = [
      {
        deviceName: '削除済みデバイス',
        deviceId: 'deletedDevice',
        deviceTypeId: '-',
        deviceTypeDisplayName: '-',
        deviceSettingGroupName: '',
        lastCommunicationAt: '',
        status: '',
        battery: 0,
        tagNameList: [],
      },
    ];
  }

  /** デバイス設定の更新を受け取る */
  public onUpdateDeviceSetting(deviceSetting: DeviceSettingViewModel) {
    if (deviceSetting.deviceId)
      this.updatedDeviceSettings[deviceSetting.deviceId] = deviceSetting;
  }

  /** フォームをViewModelへ変換する */
  private convertShipmentFormGroupToViewModel(
    shipmentFormGroup: ShipmentFormGroup,
    updatedDeviceSettings: Record<string, DeviceSettingViewModel>,
  ): EditedShipmentViewModel {
    const shipmentOptionFormGroup: ShipmentOptionFormGroup =
      shipmentFormGroup.controls.shipmentOption;

    const shipmentOption: OptionFormViewModel = {
      shipmentName: shipmentOptionFormGroup.get('shipmentName')?.value || '',
      repeatType: shipmentOptionFormGroup.get('repeatType')?.value || '',
    };

    const shipmentRouteFormGroup: ShipmentRouteFormGroup =
      shipmentFormGroup.controls.shipmentRoute;

    const shipmentRoute: RouteFormViewModel = {
      startLocationId:
        shipmentRouteFormGroup.get('startLocationId')?.value || null,
      arrivalLocationId:
        shipmentRouteFormGroup.get('arrivalLocationId')?.value || null,
    };

    const shipmentDeviceFormGroup: ShipmentDeviceFormGroup =
      shipmentFormGroup.controls.shipmentDevice;
    const shipmentDevice = {
      deviceId: shipmentDeviceFormGroup.get('deviceId')?.value || '',
      deviceType: shipmentDeviceFormGroup.get('deviceType')?.value || '',
      deviceName: shipmentDeviceFormGroup.get('deviceName')?.value || '',
    };
    const shipmentDeviceIdList = [shipmentDevice.deviceId];

    const updatedDeviceSettingList: DeviceSettingViewModel[] =
      shipmentDeviceIdList
        .filter((serialNo) => serialNo in updatedDeviceSettings)
        .map((serialNo) => this.updatedDeviceSettings[serialNo]);

    return {
      shipmentOption,
      shipmentRoute,
      shipmentDevice,
      shipmentDeviceSettingList: updatedDeviceSettingList,
    };
  }

  private convertAvailableDeviceList(
    availableDeviceList: AvailableDeviceResponse[],
  ): DeviceSettingListViewModel[] {
    // APIレスポンスは画面表示に必要な一部項目のみのため、既存ViewModelへ補完して変換する。
    const deviceSettingList = availableDeviceList.map((availableDevice) => ({
      deviceName: availableDevice.deviceName,
      deviceId: availableDevice.deviceId,
      deviceTypeId: availableDevice.deviceTypeId,
      deviceTypeDisplayName: availableDevice.deviceTypeDisplayName,
      deviceSettingGroupName: availableDevice.deviceSettingGroupName ?? '',
      lastCommunicationAt: '',
      status: '',
      battery: 0,
      tagNameList: [],
    }));
    deviceSettingList.sort((a, b) =>
      a.deviceName.localeCompare(b.deviceName, undefined, {
        numeric: true,
        ignorePunctuation: true,
      }),
    );
    return deviceSettingList;
  }

  /** 配送設定名の重複バリデーション */
  private shipmentDuplicationValidator(
    shipmentList: ShipmentNameViewModel[],
    currentShipmentId?: string,
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const shipmentName = control.value;
      const isDuplicated = shipmentList.some(
        (shipment) =>
          shipment.shipmentName === shipmentName &&
          shipment.shipmentId !== currentShipmentId,
      );

      return isDuplicated ? { isDuplicated: true } : null;
    };
  }
}
